import { useState, useRef, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Typography, Slider, IconButton, Stack } from '@mui/material';
import {
  PauseRounded,
  PlayArrowRounded,
  FastForwardRounded,
  FastRewindRounded,
  VolumeUpRounded,
  VolumeDownRounded
} from '@mui/icons-material';
import Spinner from '../spinner/Spinner';
import Visualizer from './Visualizer';
import { fetchAuth } from '../../utils/auth';
import { API } from '../../interceptors/axios';

import postImage2 from "../../static/images/rep.jpg";
import replaceImage from "../../static/images/D_logo_web.ico";

import ShareIcon from '@mui/icons-material/Share';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Tooltip, 
  Snackbar, 
  Alert 
} from '@mui/material';
import { Facebook as FacebookIcon, ContentCopy } from '@mui/icons-material';
import XIcon from '@mui/icons-material/Close';
import { Button } from '@mui/material';


const Widget = styled('div')(({ theme }) => ({
  padding: 16,
  borderRadius: 16,
  width: 343,
  maxWidth: '100%',
  margin: 'auto',
  position: 'relative',
  zIndex: 1,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.4)',
  backdropFilter: 'blur(40px)',
}));

const TinyText = styled(Typography)({
  fontSize: '0.75rem',
  opacity: 0.38,
  fontWeight: 500,
  letterSpacing: 0.2,
});

const CoverImage = styled('div')({
  width: 100,
  height: 100,
  objectFit: 'cover',
  overflow: 'hidden',
  flexShrink: 0,
  borderRadius: 8,
  backgroundColor: 'rgba(0,0,0,0.08)',
  '& > img': {
    width: '100%',
  },
});


const MusicPlayer = ({ song, isLoading, slug, songImage }) => {
  // Add loading state for server-side loading
  const [serverLoading, setServerLoading] = useState(true);

  const theme = useTheme();
  const mainIconColor = theme.palette.mode === 'dark' ? '#fff' : '#000';

  const audioRef = useRef(null);

  // Core state
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(30);
  const [localLoading, setLocalLoading] = useState(true);

  // Audio visualization state
  const [audioContext, setAudioContext] = useState(null);
  const [analyzerData, setAnalyzerData] = useState(null);

  // Add play counter state
  const [hasCountedPlay, setHasCountedPlay] = useState(false);
  const [playStartTime, setPlayStartTime] = useState(null);


  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Add share functionality
  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        setSnackbarMessage('Link copied to clipboard!');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setSnackbarMessage('Failed to copy link');
        setSnackbarOpen(true);
      });
  };

  const shareViaFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleCloseShareDialog();
  };

  const shareViaX = () => {
    const text = `Check out "${slug.replace(/-/g, ' ')}" on Distorise Music`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
    window.open(url, '_blank', 'width=600,height=400');
    handleCloseShareDialog();
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  // Add play counter function
  const updatePlayCount = async (slug) => {
    if (!slug || hasCountedPlay) return;
    
    try {
      setHasCountedPlay(true); // Set first to prevent race conditions
      const response = await API.get(`/song-counter/${slug}`);
      if (!response.ok) {
        throw new Error('Play count update failed');
      }
    } catch (error) {
      console.error('Play count error:', error);
      // Don't reset hasCountedPlay - if it fails, we don't want to retry
    }
  };

  const setupAudioContext = async () => {
    try {
      if (!audioContext) {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyzer = ctx.createAnalyser();
        analyzer.fftSize = 2048;

        if (audioRef.current) {
          const source = ctx.createMediaElementSource(audioRef.current);
          source.connect(analyzer);
          analyzer.connect(ctx.destination);

          setAnalyzerData({
            analyzer,
            bufferLength: analyzer.frequencyBinCount,
            dataArray: new Uint8Array(analyzer.frequencyBinCount)
          });
        }
        setAudioContext(ctx);
      }
    } catch (error) {
      console.error('Audio setup failed:', error);
    }
  };

  const play = async () => {
    try {
      if (!audioRef.current || localLoading) return;

      if (!isPlaying) {
        await setupAudioContext();
        await audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('Playback failed:', error);
      setIsPlaying(false);
    }
  };

  const onTimeUpdate = (e) => {
    const currentTime = e.currentTarget.currentTime;
    
    if (!isNaN(currentTime)) {
      setPosition(currentTime);
      
      // Initialize start time when playback begins
      if (currentTime < 1 && !playStartTime) {
        setPlayStartTime(Date.now());
      }
      
      // Only count after 15 real seconds have passed
      if (playStartTime && 
          !hasCountedPlay && 
          slug && 
          Date.now() - playStartTime >= 15000) {
        updatePlayCount(slug);
      }
    }
  };

  const onSliderChange = (_, value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setPosition(value);
    }
  };

  const onVolumeChange = (_, value) => {
    const vol = value / 100;
    setVolume(value);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const formatTime = (value) => {
    const minute = Math.floor(value / 60);
    const secondLeft = value - minute * 60;
    return `${minute}:${secondLeft < 10 ? `0${secondLeft}` : secondLeft}`.substring(0, 6);
  };

  // Load audio when song changes
  useEffect(() => {
      if (!song?.url) return;

      const loadAudio = async () => {
          try {
              setLocalLoading(true);
              setServerLoading(true);
              
              // Prefetch auth token
              await fetchAuth();
              const token = localStorage.getItem('token');
              
              if (!token || !audioRef.current) {
                  throw new Error('Missing token or audio reference');
              }

              // Create URL with token
              const url = new URL(song.url);
              url.searchParams.set('token', token);

              // Set up audio element
              audioRef.current.preload = "auto"; // Enable preloading
              audioRef.current.src = url.toString();

              // Handle loading states with events
              const handleCanPlay = () => {
                  setLocalLoading(false);
                  setServerLoading(false);
              };

              const handleError = (error) => {
                  // console.error('Audio loading error:', error);
                  setLocalLoading(false);
                  setServerLoading(false);
              };

              audioRef.current.addEventListener('canplay', handleCanPlay);
              audioRef.current.addEventListener('error', handleError);

              // Start loading
              await audioRef.current.load();

              // Cleanup
              return () => {
                  if (audioRef.current) {
                      audioRef.current.removeEventListener('canplay', handleCanPlay);
                      audioRef.current.removeEventListener('error', handleError);
                  }
              };

          } catch (error) {
              console.error('Audio loading failed:', error);
              setLocalLoading(false);
              setServerLoading(false);
              window.location.href = '/login';
          }
      };

      loadAudio();

      // Reset play tracking when song changes
      setHasCountedPlay(false);
      setPlayStartTime(null);

      return () => {
          if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.src = '';
          }
          if (audioContext) {
              audioContext.close();
          }
      };
  }, [song]);


  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        onTimeUpdate={onTimeUpdate}
        onLoadedData={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => {
          setIsPlaying(false);
          setPosition(0);
        }}
      />
      
      <Widget sx={{ width: { xs: "80%", sm: "84%", md: "74%" }, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', }}>

            <CoverImage>
              <img
                alt={slug ? slug.replace(/-/g, ' ') : "Song cover"}
                src={songImage || replaceImage}
                onError={(e) => {
                    // console.error("Error loading song image");
                    // e.target.src = "https://via.placeholder.com/100?text=No+Image";
                }}
                />
            </CoverImage>
            <Box sx={{ ml: 1.5, minWidth: 0 }}></Box>
            <Box></Box>

            {/* <Box sx={{ display: 'flex', alignItems: 'center' }}> */}
              {analyzerData ? <Visualizer data={analyzerData}/> : <Visualizer />}
            {/* </Box> */}

        </Box>
        <Slider
          aria-label='Audio Progress'        
          value={position}
          max={duration}
          onChange={onSliderChange}
          sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.87)' }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -2 }}>
          <TinyText>{formatTime(position)}</TinyText>
          <TinyText>-{formatTime(duration - position)}</TinyText>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: -1 }}>
          <IconButton onClick={() => audioRef.current.currentTime -= 15}>
            <FastRewindRounded fontSize="large" htmlColor={mainIconColor}/>
          </IconButton>
          
          <IconButton onClick={play} disabled={localLoading}>
            {localLoading || serverLoading ? <Spinner /> : 
              isPlaying ? 
              <PauseRounded fontSize="large" htmlColor={mainIconColor} /> : 
              <PlayArrowRounded fontSize="large" htmlColor={mainIconColor} />}
          </IconButton>
          
          <IconButton onClick={() => audioRef.current.currentTime += 15}>
            <FastForwardRounded fontSize="large" htmlColor={mainIconColor}/>
          </IconButton>

        </Box>

            {/* Share Dialog */}
            <Dialog 
              open={shareDialogOpen} 
              onClose={handleCloseShareDialog}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  backgroundColor: 'rgba(30, 30, 30, 0.95)',
                  color: 'white',
                  backdropFilter: 'blur(5px)',
                  maxWidth: '400px',
                  width: '90%'
                }
              }}
            >
              <DialogTitle sx={{ fontFamily: 'Light-1', textAlign: 'center', pt: 3 }}>
                Share "{slug.replace(/-/g, ' ')}"
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, my: 2 }}>
                  <Tooltip title="Share on Facebook">
                    <IconButton 
                      onClick={shareViaFacebook}
                      sx={{ 
                        backgroundColor: '#3b5998', 
                        color: 'white',
                        '&:hover': { backgroundColor: '#4c70ba' }
                      }}
                    >
                      <FacebookIcon />
                    </IconButton>
                  </Tooltip>
                    <Tooltip title="Share on X">
                      <IconButton 
                        onClick={shareViaX}
                        sx={{ 
                          backgroundColor: '#000000', // X uses black branding
                          color: 'white',
                          '&:hover': { backgroundColor: '#333333' }
                        }}
                      >
                        {/* Custom X icon styling for better appearance */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                          }}
                        >
                          <XIcon 
                            sx={{ 
                              fontSize: '1.2rem',
                              transform: 'scale(1.2)',
                              fontWeight: 'bold'
                            }} 
                          />
                        </Box>
                      </IconButton>
                    </Tooltip>
                  <Tooltip title="Copy Link">
                    <IconButton 
                      onClick={copyToClipboard}
                      sx={{ 
                        backgroundColor: '#6c757d', 
                        color: 'white',
                        '&:hover': { backgroundColor: '#5a6268' }
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button 
                  onClick={handleCloseShareDialog}
                  sx={{ 
                    color: '#F2D399',
                    fontFamily: 'Light',
                    '&:hover': { backgroundColor: 'rgba(242, 211, 153, 0.1)' }
                  }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert 
                onClose={handleCloseSnackbar} 
                severity="success" 
                sx={{ 
                  width: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  color: '#F2D399',
                  '& .MuiAlert-icon': {
                    color: '#F2D399'
                  }
                }}
              >
                {snackbarMessage}
              </Alert>
            </Snackbar>

        <Stack spacing={2} direction="row" sx={{ mb: 1, px: 1 }} alignItems="center">
          <VolumeDownRounded />
          <Slider
            aria-label="Volume"
            size="small"
            value={volume}
            onChange={onVolumeChange}
            sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.87)' }}
          />
          <VolumeUpRounded />
        </Stack>


          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            width: '100%',
            mt: 2,
            mb: 2,
            position: 'relative'
          }}>
            {/* Horizontal line - now solid black */}
            <Box 
              sx={{
                position: 'absolute',
                width: '80%',
                height: '1px',
                backgroundColor: '#000000', // Solid black line
                top: '50%',
                left: '10%',
                zIndex: 0
              }}
            />
            
            {/* Share button with solid black background */}
            <Tooltip 
              title="Share"
              placement="top"
              arrow
              sx={{
                '& .MuiTooltip-tooltip': {
                  backgroundColor: '#000000', // Solid black tooltip
                  color: 'white',
                  fontSize: '0.8rem',
                  padding: '6px 10px',
                  marginBottom: '4px'
                }
              }}
            >
              <IconButton 
                onClick={handleShareClick}
                sx={{ 
                  backgroundColor: '#000000', // Solid black background regardless of theme
                  color: 'white', // White icon for contrast
                  '&:hover': { 
                    backgroundColor: '#000000', // Stay black on hover
                    color: '#F2D399' // Gold color on hover
                  },
                  transition: 'all 0.2s ease',
                  padding: '6px',
                  zIndex: 1,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.5)' // Slightly stronger shadow
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

      </Widget>

    </Box>
  );
};

export default MusicPlayer;